import React from 'react';
import { Alert, Container, Button } from 'react-bootstrap';

// Préfixe de déploiement injecté par Vite ("/" en multi-conteneurs,
// "/jeu-alignement/" sous le méga-conteneur GAO). Sans lui, le bouton
// "Retour au tableau de bord" renvoie hors du préfixe → 404 du portail.
const BASE = import.meta.env.BASE_URL || '/';

/**
 * Error Boundary pour capturer les erreurs React qui causent des pages blanches
 * Les erreurs React ne sont pas toujours visibles dans la console
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Mettre à jour l'état pour afficher l'UI de fallback au prochain render
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Logger l'erreur
    console.error('🔴 Error Boundary caught an error:', error, errorInfo);

    // Stocker les détails de l'erreur dans l'état
    this.setState({
      error,
      errorInfo
    });

    // Vous pouvez aussi logger l'erreur vers un service externe
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    // Optionnel: recharger la page ou rediriger
    // window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // UI de fallback personnalisée
      return (
        <Container className="mt-5">
          <Alert variant="danger">
            <Alert.Heading>❌ Une erreur s'est produite</Alert.Heading>
            <p>
              Le composant a rencontré une erreur et ne peut pas être affiché.
              Cette erreur a été capturée pour éviter un crash complet de l'application.
            </p>

            {this.state.error && (
              <div className="mt-3">
                <h5>Détails de l'erreur:</h5>
                <pre style={{
                  background: '#f8f9fa',
                  padding: '15px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '200px'
                }}>
                  {this.state.error.toString()}
                </pre>
              </div>
            )}

            {this.state.errorInfo && (
              <div className="mt-3">
                <h5>Stack trace:</h5>
                <pre style={{
                  background: '#f8f9fa',
                  padding: '15px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '300px',
                  fontSize: '12px'
                }}>
                  {this.state.errorInfo.componentStack}
                </pre>
              </div>
            )}

            <hr />

            <div className="d-flex gap-2">
              <Button variant="primary" onClick={this.handleReset}>
                Réessayer
              </Button>
              <Button
                variant="secondary"
                onClick={() => window.location.href = `${BASE}fr/dashboard`}
              >
                Retour au tableau de bord
              </Button>
              <Button
                variant="warning"
                onClick={() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  window.location.reload();
                }}
              >
                Nettoyer le cache et recharger
              </Button>
            </div>
          </Alert>

          <Alert variant="info" className="mt-3">
            <h5>💡 Actions recommandées:</h5>
            <ul>
              <li>Copiez le message d'erreur ci-dessus</li>
              <li>Vérifiez la console du navigateur pour plus de détails</li>
              <li>Essayez de vider le cache avec le bouton ci-dessus</li>
              <li>Si le problème persiste, contactez l'équipe de développement</li>
            </ul>
          </Alert>
        </Container>
      );
    }

    // Si pas d'erreur, rendre les children normalement
    return this.props.children;
  }
}

export default ErrorBoundary;
